import { Link } from "react-router-dom";
import { ArrowRight, Clock, Check } from "lucide-react";
import { SERVICES } from "@/data/services";
import { Reveal } from "@/components/common/Reveal";
import Seo, { BreadcrumbJsonLd } from "@/components/common/Seo";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Our Detailing Services in Ottawa | Reverence Autos"
        description="Express, Interior, Full Detail and Ceramic Coating — see exactly what each mobile detailing service includes, why it matters for your car, and what it costs across Ottawa."
        path="/services"
      />
      <BreadcrumbJsonLd items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-charcoal">
        <img src="/gallery/img_7051.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-charcoal/70 via-brand-charcoal/85 to-brand-charcoal" />
        <div className="container-rv relative py-24 lg:py-32">
          <Reveal className="max-w-2xl">
            <p className="overline text-brand-red">Our services</p>
            <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
              Every detail, explained
            </h1>
            <p className="mt-5 max-w-xl text-brand-silver">
              Not sure which service your car needs? Here's exactly what each one does, why it matters, and
              what it protects — so you can choose with confidence. We come to your driveway across Ottawa.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Cards */}
      <section className="bg-white">
        <div className="container-rv grid gap-8 py-20 md:grid-cols-2 lg:py-28">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 2) * 0.08}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-brand-silver/40 bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_40px_80px_-45px_rgba(26,28,30,0.55)]"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={s.hero} alt={`${s.name} — Reverence Autos`} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {s.popular && (
                    <span className="absolute left-4 top-4 rounded-full bg-brand-red px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-white">
                      Most Popular
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-center justify-between gap-4">
                    <h2 className="font-serif text-2xl font-bold text-brand-charcoal">{s.name}</h2>
                    <span className="whitespace-nowrap font-serif text-lg font-bold text-brand-red">{s.price}</span>
                  </div>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-brand-muted">
                    <Clock className="h-3.5 w-3.5" /> {s.duration} · {s.tagline}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-brand-muted">{s.intro}</p>
                  <ul className="mt-5 space-y-2">
                    {s.benefits.map((b) => (
                      <li key={b.title} className="flex items-start gap-2 text-sm text-brand-charcoal/85">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {b.title}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">
                    Explore the {s.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-offwhite">
        <div className="container-rv py-16 text-center">
          <h2 className="font-serif text-3xl font-bold text-brand-charcoal">Still deciding?</h2>
          <p className="mx-auto mt-3 max-w-md text-brand-muted">
            Snap a photo for an instant AI estimate, or just schedule a detail — we'll recommend the right one on-site.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <Link to="/book" data-testid="services-book-cta" className="btn-red">Schedule a Detailing Now <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/pricing" className="btn-outline-dark">See full pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
